import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import axios from 'axios';
import axiosInstance from '../../../main/initializers/axios';
import JwtManager from '../../../main/utils/jwtManager';
import { config, platformHubUrl } from '../../../core/config';
import useGetUser from '../../../main/hooks/useGetUser';

/* ===================== TYPES ===================== */

export interface Notification {
  id: number;
  title?: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'mention' | 'like' | 'comment';
  unread: boolean;
  createdAt: string;
  readAt?: string;
  link?: string;
  avatar?: string;
  fromUser?: string;
  entityType?: string;
}

export interface ApiNotification {
  id: number;
  title?: string;
  message: string;
  type?: string;
  entityId?: number;
  entityType?: string;
  severity?: string;
  audienceType?: string;
  unread: boolean;
  createdAt: string;
  readAt?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (msg: string, type?: Notification['type'], link?: string, fromUser?: string) => void;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: (onlyUnread?: boolean, page?: number, pageSize?: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  clearAll: () => void;
  playNotificationSound: () => void;
  showNotificationToast: (notification: Notification) => void;
}

/* ===================== CONTEXT ===================== */

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/*
 * PORT NOTE (Architecture 5.0) — two changes against PoseidonFrontend, both at the
 * Platform boundary rather than in the component logic:
 *
 * 1. The silent count client used to be a bare `axios.create` pointed at
 *    `process.env.REACT_APP_API_URL` with the monolith path `/api/notification/count`.
 *    It deliberately bypasses the shared instance (so the count does not raise the global
 *    loader), which also means it bypasses `platformApiAdapter`. Its base and path are
 *    therefore written directly against PlatformAPI's v1 surface here.
 * 2. PlatformAPI exposes /hubs/notification for realtime unread-count refreshes.
 *    Mount and visibility refreshes also reconcile notifications missed offline.
 */
export const NOTIFICATION_HUB_ENABLED = true;

const silentNotificationApi = axios.create({
  baseURL: config.apiBaseUrl,
});

/* ===================== API ===================== */

export const getNotifications = (onlyUnread = false, page = 1, pageSize = 20) =>
  axiosInstance.get<ApiNotification[]>(`notifications?unreadOnly=${onlyUnread}&page=${page}&pageSize=${pageSize}`);

export const markNotificationAsRead = (id: number) => axiosInstance.post(`notifications/${id}/read`);
export const markAllNotificationsAsRead = () => axiosInstance.post(`notifications/read-all`);
export const getUnreadNotificationCount = () =>
  silentNotificationApi.get<{ unreadCount: number }>(`notifications/count`, {
    headers: JwtManager.accessToken ? { Authorization: `Bearer ${JwtManager.accessToken}` } : undefined,
  });

/* ===================== PROVIDER ===================== */

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const user = useGetUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const companyId = user?.companyId ?? user?.CompanyId;

  /* ===================== HELPERS ===================== */

  const mapApiToNotification = (n: ApiNotification): Notification => {
    let link: string | undefined;
    const mappedType = ((n.type ?? n.severity ?? 'info').toLowerCase() as Notification['type']);

    if (n.entityType === 'Post' && n.entityId) {
      link = `/posts/${n.entityId}`;
    }

    return {
      id: n.id,
      title: n.title,
      message: n.message,
      type: mappedType,
      unread: n.unread,
      createdAt: n.createdAt,
      readAt: n.readAt,
      link,
      entityType: n.entityType,
    };
  };

  /* ===================== ACTIONS ===================== */

  const fetchNotifications = async (onlyUnread = false, page = 1, pageSize = 20) => {
    try {
      const res = await getNotifications(onlyUnread, page, pageSize);
      const mapped = res.data.map(mapApiToNotification);
      setNotifications(mapped);
      setUnreadCount(mapped.filter((n) => n.unread).length);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data?.unreadCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch notification count', err);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false, readAt: new Date().toISOString() } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
    }
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const addNotification = (msg: string, type: Notification['type'] = 'info', link?: string, fromUser?: string) => {
    const newNotif: Notification = {
      id: Date.now(),
      title: undefined,
      message: msg,
      type,
      unread: true,
      createdAt: new Date().toISOString(),
      link,
      fromUser,
      avatar: fromUser?.charAt(0),
    };

    setNotifications((prev) => [newNotif, ...prev]);
    setUnreadCount((prev) => prev + 1);

    if (['mention', 'like', 'comment'].includes(type)) {
      playNotificationSound();
    }

    showNotificationToast(newNotif);
  };

  /* ===================== UI HELPERS ===================== */

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch {
    }
  };

  const showNotificationToast = (notification: Notification) => {
    const toast = document.createElement('div');
    toast.innerText = notification.message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 4000);
  };

  useEffect(() => {
    /*
     * The legacy guard was `if (!companyId || !JwtManager.accessToken)`. A Platform
     * identity has no numeric company id (see the bridge's `toLegacyUser`), so keying the
     * whole effect on it would also skip the unread-count fetch. The session token is the
     * real precondition now.
     */
    if (!JwtManager.accessToken) {
      setUnreadCount(0);
      return;
    }

    let mounted = true;
    let retryStartId: ReturnType<typeof setTimeout> | null = null;
    let connection: signalR.HubConnection | null = NOTIFICATION_HUB_ENABLED
      ? new signalR.HubConnectionBuilder()
          .withUrl(platformHubUrl('/hubs/notification'), {
            accessTokenFactory: () => JwtManager.accessToken ?? '',
            withCredentials: false,
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect()
          .build()
      : null;

    const refreshUnreadCount = async () => {
      if (!mounted) return;
      await fetchUnreadCount();
    };

    const onVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };

    /*
     * The hub is optional here (see `NOTIFICATION_HUB_ENABLED`). When it is disabled the
     * unread count still refreshes on mount and on tab focus, which is what the bell
     * renders; only push delivery is missing.
     */
    const hub = connection;
    if (hub) {
    hub.on('companyNotification', async (payload?: { companyId?: number }) => {
      if (!mounted) return;
      if (payload?.companyId && payload.companyId !== companyId) return;
      setUnreadCount((prev) => prev + 1);
      await refreshUnreadCount();
    });

    const scheduleStart = () => {
      if (!mounted) return;
      if (retryStartId) {
        clearTimeout(retryStartId);
      }
      retryStartId = setTimeout(() => {
        void startConnection();
      }, 5000);
    };

    const startConnection = async () => {
      if (!mounted) return;

      try {
        if (hub.state === signalR.HubConnectionState.Connected || hub.state === signalR.HubConnectionState.Connecting) {
          return;
        }

        await hub.start();
        await hub.invoke('JoinCompany', companyId);
        await refreshUnreadCount();
      } catch (err) {
        if ((err as any)?.name !== 'AbortError') {
          console.error('Failed to start notification hub', err);
        }
        scheduleStart();
      }
    };

    hub.onreconnected(async () => {
      if (!mounted) return;
      try {
        if (hub.state === signalR.HubConnectionState.Connected) {
          await hub.invoke('JoinCompany', companyId);
        }
        await refreshUnreadCount();
      } catch {
      }
    });

    hub.onclose(() => {
      scheduleStart();
    });

    void (async () => {
      await refreshUnreadCount();
      await startConnection();
    })();
    }

    /*
     * With the hub disabled the count is still fetched, so the bell is accurate on load and
     * on every tab focus — only push delivery is missing.
     */
    if (!hub) void refreshUnreadCount();

    document.addEventListener('visibilitychange', onVisibilityRefresh);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibilityRefresh);
      if (retryStartId) {
        clearTimeout(retryStartId);
      }
      if (!connection) return;
      const closingConnection = connection;
      connection = null;
      void (async () => {
        try {
          if (closingConnection.state === signalR.HubConnectionState.Connected) {
            await closingConnection.invoke('LeaveCompany', companyId);
          }
        } catch {
        }

        try {
          await closingConnection.stop();
        } catch {
        }
      })();
    };
  }, [companyId]);

  /* ===================== PROVIDER ===================== */

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAllAsRead,
        fetchNotifications,
        markAsRead,
        clearAll,
        playNotificationSound,
        showNotificationToast,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

/* ===================== HOOK ===================== */

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
